/**
 * TemplatesTab -- Apply/import user account templates in the UserAdminPanel.
 *
 * Shows a "Save as Template" action at the top and a list of compatible
 * templates that can be applied to the current user. Each template card
 * shows field counts, server type compatibility, and admin actions.
 *
 * Visual treatment matches sibling tabs (OverviewTab, ControlsTab, DevicesTab)
 * with accent left-border stripes, tinted icon backgrounds, and card layouts.
 */
import { useCallback, useState } from "react";

import { Badge } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { Icon } from "../../ui/Icon";
import { Input } from "../../ui/Input";
import { Spinner } from "../../ui/Spinner";
import { Tooltip } from "../../ui/Tooltip";
import { useDialog } from "../../ui/Dialog";
import { cn } from "../../../utils/cn";
import { toast } from "../../../lib/toast";
import {
  useTemplates,
  useImportTemplateFromUser,
  useApplyTemplate,
  useDeleteTemplate,
  useDuplicateTemplate,
  type WsdUserTemplate,
  type ApplyTemplateResult,
} from "../../../hooks/useUserTemplates";
import { TemplateManager } from "../TemplateManager";
import { formatRelativeTime } from "./helpers";

// ============================================================================
// Types
// ============================================================================

export type TemplatesTabProps = {
  userId: string;
  userName: string;
  serverType: string;
  onRefreshProfile: () => void;
  availableLibraries?: Array<{
    id: string;
    name: string;
    subFolders?: Array<{ compositeId: string; path: string }>;
  }>;
};

// ============================================================================
// Accent system (matches ControlsTab / DevicesTab)
// ============================================================================

const accentMap = {
  amber: {
    border: "border-l-amber-500",
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-400",
  },
  sky: {
    border: "border-l-sky-500",
    iconBg: "bg-sky-500/10",
    iconColor: "text-sky-400",
  },
  violet: {
    border: "border-l-violet-500",
    iconBg: "bg-violet-500/10",
    iconColor: "text-violet-400",
  },
  emerald: {
    border: "border-l-emerald-500",
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-400",
  },
  slate: {
    border: "border-l-slate-500",
    iconBg: "bg-muted/20",
    iconColor: "text-muted-foreground",
  },
} as const;

// ============================================================================
// Helpers
// ============================================================================

function countFields(template: WsdUserTemplate): {
  policyCount: number;
  configCount: number;
  wsdCount: number;
} {
  const policyCount = Object.keys(template.policyFields).length;
  const configCount = Object.keys(template.configFields).length;
  const wsdCount = Object.keys(template.wsdLocalFields).length;
  return { policyCount, configCount, wsdCount };
}

function formatApplyResult(result: ApplyTemplateResult): string {
  const parts: string[] = [];
  if (result.policyFieldCount > 0) {
    parts.push(`${result.policyFieldCount} policy`);
  }
  if (result.configFieldCount > 0) {
    parts.push(`${result.configFieldCount} config`);
  }
  const fieldSummary = parts.length > 0
    ? `Applied ${parts.join(" + ")} fields.`
    : "No fields applied.";

  const warnings: string[] = [];
  if (result.unresolvedLibraries.length > 0) {
    warnings.push(`${result.unresolvedLibraries.length} libraries unresolved.`);
  }
  if (result.policyError) {
    warnings.push(`Policy: ${result.policyError}`);
  }
  if (result.configError) {
    warnings.push(`Config: ${result.configError}`);
  }

  return warnings.length > 0
    ? `${fieldSummary} ${warnings.join(" ")}`
    : fieldSummary;
}

/** Section header with tinted icon badge, matching sibling tab patterns */
const SectionHeader = ({
  icon,
  accent,
  children,
}: {
  icon: string;
  accent: keyof typeof accentMap;
  children: React.ReactNode;
}) => {
  const a = accentMap[accent];
  return (
    <div className="flex items-center gap-2 mb-1.5">
      <div className={cn("rounded-md flex items-center justify-center h-5 w-5 shrink-0", a.iconBg)}>
        <Icon icon={icon} className={cn("h-3 w-3", a.iconColor)} />
      </div>
      <h4 className="text-[10px] 2xl:text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
        {children}
      </h4>
    </div>
  );
};

// ============================================================================
// TemplateCard
// ============================================================================

type TemplateCardProps = {
  template: WsdUserTemplate;
  userName: string;
  onApply: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  applyBusy: boolean;
};

const TemplateCard = ({
  template,
  userName,
  onApply,
  onEdit,
  onDuplicate,
  onDelete,
  applyBusy,
}: TemplateCardProps) => {
  const { policyCount, configCount, wsdCount } = countFields(template);
  const totalFields = policyCount + configCount + wsdCount;

  const accentKey = template.serverType === "emby"
    ? "emerald" as const
    : template.serverType === "jellyfin"
      ? "violet" as const
      : template.serverType === "plex"
        ? "amber" as const
        : "sky" as const;
  const a = accentMap[accentKey];

  return (
    <div
      className={cn(
        "rounded-lg border border-border/50 bg-card",
        "border-l-2",
        a.border,
        "p-3 flex flex-col gap-2",
      )}
    >
      {/* Row 1: Name + badges */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs 2xl:text-sm font-semibold text-foreground truncate">
              {template.name}
            </span>
            {template.isDefault && (
              <Badge variant="primary" size="xs">Default</Badge>
            )}
          </div>
          {template.description && (
            <p className="text-[10px] 2xl:text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {template.description}
            </p>
          )}
        </div>
        {template.serverType && (
          <Badge
            variant={
              template.serverType === "emby"
                ? "success"
                : template.serverType === "plex"
                  ? "warning"
                  : "muted"
            }
            size="xs"
            className="shrink-0 capitalize"
          >
            {template.serverType}
          </Badge>
        )}
      </div>

      {/* Row 2: Field counts */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1 text-[10px] 2xl:text-xs text-muted-foreground bg-muted/20 px-1.5 py-0.5 rounded">
          <Icon icon="lucide:shield" className="h-2.5 w-2.5 opacity-60 shrink-0" />
          {policyCount} policy
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] 2xl:text-xs text-muted-foreground bg-muted/20 px-1.5 py-0.5 rounded">
          <Icon icon="lucide:settings" className="h-2.5 w-2.5 opacity-60 shrink-0" />
          {configCount} config
        </span>
        {wsdCount > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] 2xl:text-xs text-muted-foreground bg-muted/20 px-1.5 py-0.5 rounded">
            <Icon icon="lucide:zap" className="h-2.5 w-2.5 opacity-60 shrink-0" />
            {wsdCount} wsd
          </span>
        )}
        <span className="text-[10px] 2xl:text-xs text-muted-foreground">
          {totalFields} total
        </span>
      </div>

      {/* Row 3: Metadata */}
      <div className="flex items-center gap-3 text-[10px] 2xl:text-xs text-muted-foreground">
        {template.createdBy && (
          <span className="flex items-center gap-1">
            <Icon icon="lucide:user" className="h-2.5 w-2.5" />
            {template.createdBy}
          </span>
        )}
        <Tooltip content={new Date(template.createdAt < 1e12 ? template.createdAt * 1000 : template.createdAt).toLocaleString()}>
          <span className="cursor-default">
            {formatRelativeTime(template.createdAt)}
          </span>
        </Tooltip>
      </div>

      {/* Row 4: Actions */}
      <div className="flex items-center justify-between gap-2 border-t border-border/30 pt-2 mt-0.5">
        <div className="flex items-center gap-1">
          <Tooltip content="Edit template">
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
            >
              <Icon icon="lucide:pencil" className="h-3 w-3" />
            </button>
          </Tooltip>
          <Tooltip content="Duplicate template">
            <button
              type="button"
              onClick={onDuplicate}
              className="inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
            >
              <Icon icon="lucide:copy" className="h-3 w-3" />
            </button>
          </Tooltip>
          <Tooltip content="Delete template">
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center justify-center h-7 w-7 rounded-md text-destructive hover:bg-destructive/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
            >
              <Icon icon="lucide:trash-2" className="h-3 w-3" />
            </button>
          </Tooltip>
        </div>
        <Button
          variant="primary"
          size="sm"
          className="h-7 text-xs"
          onClick={onApply}
          disabled={applyBusy}
        >
          {applyBusy ? (
            <Spinner size="3" className="mr-1.5" />
          ) : (
            <Icon icon="lucide:play" className="h-3 w-3 mr-1.5" />
          )}
          Apply to {userName}
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// SaveAsTemplateForm (inline)
// ============================================================================

const SaveAsTemplateForm = ({
  userName,
  importing,
  onSave,
  onCancel,
}: {
  userName: string;
  importing: boolean;
  onSave: (name: string, description: string) => void;
  onCancel: () => void;
}) => {
  const [name, setName] = useState(`${userName}'s Settings`);
  const [description, setDescription] = useState("");

  return (
    <div className={cn(
      "rounded-lg border border-border/50 bg-card border-l-2",
      accentMap.amber.border,
      "p-3 space-y-2.5",
    )}>
      <div>
        <label className="text-[10px] 2xl:text-xs font-medium uppercase tracking-wide text-muted-foreground block mb-1">
          Template Name
        </label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Template name..."
          className="text-xs"
          disabled={importing}
          autoFocus
        />
      </div>
      <div>
        <label className="text-[10px] 2xl:text-xs font-medium uppercase tracking-wide text-muted-foreground block mb-1">
          Description (optional)
        </label>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description..."
          className="text-xs"
          disabled={importing}
        />
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button
          variant="secondary"
          size="sm"
          className="h-7 text-xs"
          onClick={onCancel}
          disabled={importing}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          className="h-7 text-xs"
          onClick={() => onSave(name.trim(), description.trim())}
          disabled={importing || !name.trim()}
        >
          {importing ? (
            <Spinner size="3" className="mr-1.5" />
          ) : (
            <Icon icon="lucide:save" className="h-3 w-3 mr-1.5" />
          )}
          Save Template
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// Component
// ============================================================================

export const TemplatesTab = ({
  userId,
  userName,
  serverType,
  onRefreshProfile,
  availableLibraries,
}: TemplatesTabProps) => {
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [editorMode, setEditorMode] = useState<"list" | "create" | "edit">("list");
  const [editingTemplate, setEditingTemplate] = useState<WsdUserTemplate | null>(null);

  const { templates, isLoading, error, refetch } = useTemplates(undefined, true);
  const { importing, importFromUser } = useImportTemplateFromUser();
  const { applying, applyTemplate } = useApplyTemplate();
  const { deleting, deleteTemplate } = useDeleteTemplate();
  const { duplicating, duplicateTemplate } = useDuplicateTemplate();
  const dialog = useDialog();

  // Filter templates compatible with the current server type
  const compatibleTemplates = templates.filter(
    (t) => t.serverType === null || t.serverType === serverType
  );

  // -- Save as template
  const handleSaveAsTemplate = useCallback(
    async (name: string, description: string) => {
      try {
        await importFromUser(serverType, userId, name, description || undefined);
        toast.success("Template saved from user settings");
        setShowSaveForm(false);
        refetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to save template");
      }
    },
    [serverType, userId, importFromUser, refetch]
  );

  // -- Apply template
  const handleApply = useCallback(
    async (template: WsdUserTemplate) => {
      const confirmed = await dialog.confirm({
        title: `Apply "${template.name}"?`,
        description: `This will update ${userName}'s policy and configuration settings to match this template. Existing values will be overwritten for any fields defined in the template.`,
        tone: "warning",
        confirmText: "Apply Template",
      });
      if (!confirmed) return;

      try {
        const result = await applyTemplate(template.id, serverType, userId);
        if (result) {
          toast.success(formatApplyResult(result));
          onRefreshProfile();
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to apply template");
      }
    },
    [dialog, userName, applyTemplate, serverType, userId, onRefreshProfile]
  );

  // -- Duplicate template
  const handleDuplicate = useCallback(
    async (template: WsdUserTemplate) => {
      const name = await dialog.prompt({
        title: "Duplicate Template",
        description: `Create a copy of "${template.name}" with a new name.`,
        defaultValue: `${template.name} (Copy)`,
        placeholder: "New template name...",
        confirmText: "Duplicate",
      });
      if (!name) return;

      try {
        await duplicateTemplate(template.id, name);
        toast.success("Template duplicated");
        refetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to duplicate template");
      }
    },
    [dialog, duplicateTemplate, refetch]
  );

  // -- Delete template
  const handleDelete = useCallback(
    async (template: WsdUserTemplate) => {
      const confirmed = await dialog.confirm({
        title: "Delete template?",
        description: `Permanently delete "${template.name}"? This cannot be undone.`,
        tone: "danger",
        confirmText: "Delete",
      });
      if (!confirmed) return;

      try {
        await deleteTemplate(template.id);
        toast.success("Template deleted");
        refetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to delete template");
      }
    },
    [dialog, deleteTemplate, refetch]
  );

  // -- Open editor for create / edit
  const handleOpenCreate = useCallback(() => {
    setEditingTemplate(null);
    setEditorMode("create");
  }, []);

  const handleOpenEdit = useCallback((template: WsdUserTemplate) => {
    setEditingTemplate(template);
    setEditorMode("edit");
  }, []);

  const handleEditorSave = useCallback(() => {
    setEditorMode("list");
    setEditingTemplate(null);
    refetch();
  }, [refetch]);

  const handleEditorClose = useCallback(() => {
    setEditorMode("list");
    setEditingTemplate(null);
  }, []);

  // -- Editor view (create or edit)
  if (editorMode !== "list") {
    return (
      <TemplateManager
        template={editorMode === "edit" ? editingTemplate : null}
        onSave={handleEditorSave}
        onClose={handleEditorClose}
        availableLibraries={availableLibraries}
        serverType={serverType}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Save / Create actions — 2-column layout */}
      {showSaveForm ? (
        <div>
          <SectionHeader icon="lucide:save" accent="amber">Save as Template</SectionHeader>
          <SaveAsTemplateForm
            userName={userName}
            importing={importing}
            onSave={handleSaveAsTemplate}
            onCancel={() => setShowSaveForm(false)}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Save as Template */}
          <div className={cn(
            "rounded-lg border border-border/50 bg-card border-l-2 p-3",
            accentMap.amber.border,
          )}>
            <div className="flex items-center gap-2 mb-2">
              <div className={cn("rounded-md flex items-center justify-center h-5 w-5 shrink-0", accentMap.amber.iconBg)}>
                <Icon icon="lucide:save" className={cn("h-3 w-3", accentMap.amber.iconColor)} />
              </div>
              <h4 className="text-xs 2xl:text-sm font-semibold text-foreground">
                Save as Template
              </h4>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="h-7 text-xs w-full"
              onClick={() => setShowSaveForm(true)}
            >
              <Icon icon="lucide:download" className="h-3.5 w-3.5 mr-1.5" />
              Save {userName}&apos;s Settings
            </Button>
            <p className="text-[10px] 2xl:text-xs text-muted-foreground mt-2 leading-relaxed">
              Captures this user&apos;s current policy and configuration as a reusable template for new users and device setups.
            </p>
          </div>

          {/* Create from scratch */}
          <div className={cn(
            "rounded-lg border border-border/50 bg-card border-l-2 p-3",
            accentMap.emerald.border,
          )}>
            <div className="flex items-center gap-2 mb-2">
              <div className={cn("rounded-md flex items-center justify-center h-5 w-5 shrink-0", accentMap.emerald.iconBg)}>
                <Icon icon="lucide:plus-circle" className={cn("h-3 w-3", accentMap.emerald.iconColor)} />
              </div>
              <h4 className="text-xs 2xl:text-sm font-semibold text-foreground">
                Create Template
              </h4>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="h-7 text-xs w-full"
              onClick={handleOpenCreate}
            >
              <Icon icon="lucide:plus" className="h-3.5 w-3.5 mr-1.5" />
              Create from Scratch
            </Button>
            <p className="text-[10px] 2xl:text-xs text-muted-foreground mt-2 leading-relaxed">
              Build a template from scratch with custom policy and configuration values, independent of any existing user.
            </p>
          </div>
        </div>
      )}

      {/* Available Templates */}
      <div>
        <SectionHeader icon="lucide:copy-check" accent="sky">
          Available Templates ({compatibleTemplates.length})
        </SectionHeader>

        {/* Loading */}
        {isLoading && templates.length === 0 && (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Spinner size="4" className="mr-2" />
            <span className="text-xs 2xl:text-sm">Loading templates...</span>
          </div>
        )}

        {/* Error */}
        {error && templates.length === 0 && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-3">
            <div className="flex items-center gap-2">
              <Icon icon="lucide:alert-triangle" className="h-4 w-4 text-destructive shrink-0" />
              <div>
                <p className="text-xs 2xl:text-sm font-medium text-destructive-text">
                  Failed to load templates
                </p>
                <p className="text-[10px] 2xl:text-xs text-destructive-text mt-0.5">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Empty */}
        {!isLoading && !error && compatibleTemplates.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Icon icon="lucide:file-x" className="h-8 w-8 text-muted-foreground/30 mb-2" />
            <p className="text-xs 2xl:text-sm text-muted-foreground">No compatible templates</p>
            <p className="text-[10px] 2xl:text-xs text-muted-foreground mt-0.5">
              Save a user&apos;s settings as a template to get started
            </p>
          </div>
        )}

        {/* Template cards */}
        {compatibleTemplates.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {compatibleTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                userName={userName}
                onApply={() => void handleApply(template)}
                onEdit={() => handleOpenEdit(template)}
                onDuplicate={() => void handleDuplicate(template)}
                onDelete={() => void handleDelete(template)}
                applyBusy={applying || duplicating || deleting}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
